<script lang="ts">
	/**
	 * AddEditButtonsNew Component
	 *
	 * This component provides a unified interface for room action/reorder operations
	 * in the contexts of room. It manages the display of various buttons
	 * based on edit state and user permissions.
	 *
	 * Key Features:
	 * - Reorder items
	 * - Finish Reordering items
	 * - Button with dropdown for room actions
	 */

	import { createEventDispatcher, onDestroy } from 'svelte';
	import { get } from 'svelte/store';
	import { format } from 'svelte-i18n';
	//Stores
	import {
		edit,
		initialEdit,
		bulkActionEdit,
		update,
		resetEditUpdateStore,
	} from 'src/stores/ui/edit_lists';
	//Design system components
	import { Button, ButtonV2, Checkbox, IconButton } from '@Shelly/shelly-design-system';
	import { resetDrawer } from 'src/helpers/drawer_helpers';
	//BatchCommandButtons dependencies
	import OptionManagmentIconButton from './OptionManagmentIconButton.svelte';
	import TabsActionsButton from './TabsActionsButton.svelte';
	import { is_mobile } from 'src/stores';
	// Helpers
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';
	import { resetFiltersAllDevicesList } from 'src/stores/ui/filters';
	import { getButtonText } from 'src/helpers/device_data_helpers';
	import { requestExitEditMode } from 'src/helpers/edit_chrome_helpers';
	import type { UpdateModeSubTypes } from 'src/stores/ui/edit_lists';
	import type { IconButtonPropsTypes } from '@Shelly/shelly-design-system';

	/**
	 * Event dispatcher for add button clicks
	 * Emits 'add_click', 'edit_room_click' or 'delete_room_click' event when button is pressed
	 */
	const dispatch = createEventDispatcher<{
		add_click: {};
		edit_room_click: {};
		delete_room_click;
		managed_click: {};
	}>();
	const dispatchSubmit = createEventDispatcher<{ 'submit-click': {} }>();
	const dispatchSelectAllClick = createEventDispatcher<{ 'select-all': {} }>();

	// Button visibility and state props
	export let disableEditButton = false; // Disable the edit button
	/** @deprecated Finish now shows for any non-bulk `$edit`; kept for call-site compat. */
	export let showFinishReorderBtn = false;
	/**
	 * When true (room header chrome), Finish is rendered by `RoomHeaderChrome`
	 * instead — avoids remount / store-desync killing the only exit control.
	 */
	export let hideFinishButton = false;
	export let showBulkActions = false;
	export let areFiltersDisabled: boolean = false;
	export let disableReorderButton = false; // Disable the reorder button when there's 1 or fewer items
	export let managed = false; // Shared homes: render only a three-dots button that defers to the owner-managed modal
	/**
	 * Optional "Change view" (or other) section forwarded to the underlying
	 * `OptionManagmentIconButton` so the room devices list can surface view
	 * selection inside the kebab dropdown instead of a separate control.
	 */
	export let viewSelectionGroup: any = null;
	/** Trigger IconButton variant for the kebab. Defaults to `secondary`; the
	 *  tabs-action-slot usage passes `help-2` for a transparent trigger. */
	export let triggerVariant: IconButtonPropsTypes['variant'] = 'secondary';

	let windowClientWidth: number; // Current window width for responsive behavior

	let customButtonText: string = '';

	const AIModelCommands = [
		{
			name: 'text_enable_ai_model',
			id: 'enable_ai_model' as UpdateModeSubTypes,
		},
		{
			name: 'text_disable_ai_model',
			id: 'disable_ai_model' as UpdateModeSubTypes,
		},
		{
			name: 'manage_ai_notifications',
			id: 'manage_ai_notifications' as UpdateModeSubTypes,
		},
	];

	const getAICommandText = (subType: UpdateModeSubTypes): string => {
		const command = AIModelCommands.find((cmd) => cmd.id === subType);
		return command ? $format(command.name) : '';
	};

	$: if ($update.type === 'ai_device_failure' && $update.subType) {
		customButtonText = getAICommandText($update.subType);
	} else if (!$update.value) {
		customButtonText = '';
	}

	onDestroy(() => {
		// Clear only when leaving edit. Remounting while `$edit` is still true
		// (e.g. room header chrome swapping wrappers) must not drop `$initialEdit`
		// or related edit chrome state races on re-enter.
		if (!get(edit)) {
			initialEdit.set(false);
		}
		customButtonText = '';
	});

	$: selectedDevicesLength = Object.keys($update.selectedDevices).length;
	// Enhanced reactive statement that uses custom text when available
	$: buttonText = customButtonText || getButtonText($update.type, $format);
</script>

<!-- // TODO If info icon stays, add utility classes and fix grid gap in toolbar when slot is hidden-->
<svelte:window bind:innerWidth={windowClientWidth} />

{#if managed}
	<!-- Shared homes: keep the affordance but explain that room actions are owner-managed -->
	<TabsActionsButton variant={triggerVariant} on:click={() => dispatch('managed_click')} />
{:else}
	<!--
		Exit reorder whenever we're in non-bulk edit (unless the room header
		owns Finish via hideFinishButton).
	-->
	{#if $edit && !$bulkActionEdit && !hideFinishButton}
		<ButtonV2
			size="m"
			variant="primary"
			width="full"
			icon="fas fa-xmark"
			on:click={() => requestExitEditMode()}
		>
			{$format('label_finish_reordering', { default: 'Finish reordering' })}
		</ButtonV2>
	{/if}

	{#if !$edit}
		<!-- TODO: Pass correct additional dropdown group based on context -->
		<OptionManagmentIconButton
			bind:areFiltersDisabled
			addBulkCommandsOptions={showBulkActions}
			disableBulkActions={disableEditButton}
			{disableReorderButton}
			{viewSelectionGroup}
			{triggerVariant}
			on:edit-click={(e) => {
				if (e.detail.title === 'prompt_title_edit_room') {
					dispatch('edit_room_click');
				}
			}}
			on:delete-click={(e) => {
				if (e.detail.title === 'button_delete_room') {
					dispatch('delete_room_click');
				}
			}}
			on:additional-dropdown-item-click={(e) => {
				if (e.detail.title === 'reorder_room_items_title') {
					resetDrawer();
					// initialEdit before edit so subscribers that remount never see edit∧¬initialEdit.
					initialEdit.set(true);
					edit.set(true);
				}
			}}
		/>
	{/if}

	{#if $bulkActionEdit && $edit}
		<div
			class={classMap({
				'd-flex align-items-center': true,
				'flex-wrap': $is_mobile,
			})}
		>
			<div class="d-flex width-full">
				<Checkbox
					size="m"
					id="checkbox-select-all"
					class="flex-shrink-0 mr-s text-capitalize"
					checked={$update.selectAll}
					disabled={$update.subType === 'enable_ai_model'}
					on:click={(e) => {
						update.update((s) => {
							return {
								...s,
								selectAll: (e.target as HTMLInputElement).checked,
							};
						});
						edit.set(true);
						dispatchSelectAllClick('select-all', e);
					}}
				>
					{$format('lebel_select_all', { default: 'select all' })}
				</Checkbox>
				<Button
					variant="primary"
					size="m"
					class="width-md-auto width-sm-full mr-sm-s mr-md-s text-capitalize"
					on:click={() => {
						dispatchSubmit('submit-click');
					}}
					{...selectedDevicesLength > 0
						? {
								notification: {
									type: 'danger',
									title: selectedDevicesLength,
								},
							}
						: {}}
					disabled={selectedDevicesLength === 0}
				>
					{buttonText}
				</Button>
				<IconButton
					aria-label={$format('cancel', { default: 'Cancel' })}
					variant="secondary"
					icon="fas fa-times"
					on:click={() => {
						bulkActionEdit.set(false);
						areFiltersDisabled = false;
						resetEditUpdateStore();
						resetFiltersAllDevicesList();
						customButtonText = '';
					}}
				/>
			</div>
		</div>
	{/if}
{/if}
